/* eslint-disable spire/export-styles */
import { HasProductContext, withProductContext } from "@DSG/Components/Coveo/CoveoContext/CoveoProductContext";
import CoveoApplicationState from "@DSG/Store/Data/CoveoApplicationState/CoveoApplicationState";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { canAddToCart } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
// import ExecuteOnSuccessCallback, { chain as addToCart } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: CoveoApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
        customSettings,
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    const Product: any = {
        salesIndicator: String(props.coveoproductContext.coveoProduct.saleType),
        productDetailUrl: props.coveoproductContext.coveoProduct.urlSegment,
        shortDescription: props.coveoproductContext.coveoProduct.shortDescription,
        erpNumber: props.coveoproductContext.coveoProduct.productId,
        unitOfMeasureDescription: props.coveoproductContext.coveoProduct.unitofmeasure,
        unitOfMeasureDisplay: props.coveoproductContext.coveoProduct.unitofmeasure,
        unitOfMeasure: props.coveoproductContext.coveoProduct.unitofmeasure,
        qtyOrdered: Number(props.coveoproductContext.coveoProduct.minimumOrderQty),
        pricing: props.coveoproductContext.coveoProduct.pricing ?? undefined,
        productUri: props.coveoproductContext.coveoProduct.canonicalUrl,
    };

    const currentPage = getCurrentPage(state).type;

    // Determine if the current page is a Product Details Page (PDP) or Brand/Product List Page (PLP).
    const isPdp = currentPage === "ProductDetailsPage" || currentPage === "BrandDetailsPage";

    return {
        canAddToCart: canAddToCart(
            state,
            Product,
            props.coveoproductContext.coveoProductInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        customSettings,
        isPdp,
        coveoCartController: state.DSGGlobalData?.CartController,
        currentCart: getCurrentCartState(state).value,
        showPopUp: state.addToCartPopUp?.showPopup
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

export const coveoProductAddToCartButtonStyles: ButtonPresentationProps = {};

const CoveoProductAddToCartButton = ({
    coveoproductContext: {
        coveoProduct,
        coveoProduct: { productId, quoteRequired, allowZeroPricing, pricing },
        coveoProductInfo: { qtyOrdered, unitOfMeasure },
    },
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    customSettings,
    isPdp,
    coveoCartController,
    currentCart,
    showPopUp,
    ...otherProps
}: Props) => {
    const dispatch = useDispatch();
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(coveoProductAddToCartButtonStyles, extendedStyles));
    const settings = customSettings as any;
    const addToCartModalVisibleTimeInMilliSeconds = settings.addToCartModalVisibleTimeInMilliSeconds;
    const addToCartClickHandler = async () => {    
        // Check if product exists in current cart
        const existingCartLine = currentCart?.cartLines?.find(line => line.productId === productId);
        const existingQuantity = existingCartLine ? existingCartLine.qtyOrdered || 0 : 0;
        
        onAddToCartClick?.();

        // Add analytics tracking
        sessionStorage.setItem("item_list_name", isPdp ? "Product Detail Page" : "Product List Page");
        
        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        // Send product click event on add to cart from non-PDP pages
        if (!isPdp) {
            coveoProduct.interactiveProduct?.select();
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        if (cartline) {
            sessionStorage.setItem("addToCartModalCartLine", JSON.stringify({ cartLine: cartline }));
            dispatch({
                        type: "AddToCartPopup/Popup/ShowPopup",
                        showPopup: true
                    });
             dispatch({
                        type: "AddToCartPopup/Popup/CartLine",
                        cartLine: cartline
                    });
        }
        
        // Send event to Coveo for add to cart
        if (cartline && coveoCartController) {            
            // Calculate new quantity
            const newQuantity = cartline.qtyOrdered || 0;
            const isNewItem = existingQuantity === 0;
            const quantityChanged = newQuantity !== existingQuantity;
            
            // Update Coveo cart
            if (isNewItem || quantityChanged) {
                // console.log(`Sending direct Coveo cart update for ${cartline.productId}, quantity: ${newQuantity}, previous: ${existingQuantity}`);
                coveoCartController.updateItemQuantity({
                    productId: coveoProduct.productNumber || "",
                    name: coveoProduct.title,
                    price: cartline.pricing?.actualPrice || 0,
                    quantity: newQuantity
                });
            }
        } else {
            console.log("No cartline or coveoCartController available, skipping direct update");
        }

        if (showAddToCartConfirmationDialog) {
            let addToCartModal: any;
            // if (typeof window !== 'undefined')
            // {
                addToCartModal = document.querySelector(".addToCartModal");
                if (addToCartModal) {
                    addToCartModal.classList.add('showAddToCartModal');

                    const timer = setTimeout(() => {
                        addToCartModal.classList.remove('showAddToCartModal');
                    }, addToCartModalVisibleTimeInMilliSeconds);

                    // Cleanup the timer when the component unmounts or when the modal is hidden
                    return () => clearTimeout(timer);
                }
            // }
        }
    };

    return (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
            {...otherProps}
        >
            {labelOverride ?? translate("Add to cart")}
        </Button>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(CoveoProductAddToCartButton));
